import { useEffect, useMemo, useState } from 'react';
import { FiCopy, FiShield, FiChevronDown, FiHelpCircle } from 'react-icons/fi';
import { Modal } from './Modal';
import { Button } from './Button';
import { useI18n } from '../i18n/useI18n';
import { useToast } from '../hooks/useToast';
import { createCopyConfig } from '../utils/api';

const MAX_NAME_LENGTH = 32;
const MIN_AMOUNT = 1;
const DEFAULT_MARKET_COOLDOWN_MINUTES = 3;
const MIN_MARKET_COOLDOWN_MINUTES = 1;
const MAX_MARKET_COOLDOWN_MINUTES = 30;

const formatPercent = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  return `${Number(value).toFixed(digits)}%`;
};

const formatUSD = (value, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const num = Number(value);
  return `${num >= 0 ? '+' : '-'}$${Math.abs(num).toFixed(digits)}`;
};

const shortAddress = (address) => {
  if (!address) return '';
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export function CopyTradeConfigModal({
  open,
  onClose,
  trader,
  onCreated,
  onRequireMembership,
}) {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [configName, setConfigName] = useState('');
  const [amountPerMarket, setAmountPerMarket] = useState('1');
  const [investmentTotalLimit, setInvestmentTotalLimit] = useState('');
  const [stopLossEnabled, setStopLossEnabled] = useState(false);
  const [stopLossLimit, setStopLossLimit] = useState('');
  const [copySellEnabled, setCopySellEnabled] = useState(true);
  const [marketCooldownEnabled, setMarketCooldownEnabled] = useState(false);
  const [marketCooldownMinutes, setMarketCooldownMinutes] = useState(
    String(DEFAULT_MARKET_COOLDOWN_MINUTES)
  );
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const resolvedTraders = useMemo(() => {
    if (!trader) return [];
    if (Array.isArray(trader.traders)) {
      return trader.traders.filter((item) => item && item.address);
    }
    if (Array.isArray(trader.addresses)) {
      return trader.addresses
        .filter(Boolean)
        .map((address) => ({ address }));
    }
    if (trader.address) {
      return [trader];
    }
    return [];
  }, [trader]);

  const isPortfolio = Boolean(trader?.mode === 'portfolio' || resolvedTraders.length > 1);
  const traderCount = resolvedTraders.length;
  const primaryTrader = resolvedTraders[0];

  useEffect(() => {
    if (!open) return;
    const baseName = isPortfolio
      ? t('copyConfigModal.portfolioDefaultName', { count: traderCount || 1 })
      : (primaryTrader?.name
          ? t('copyConfigModal.defaultName', { name: primaryTrader.name })
          : t('copyConfigModal.defaultNameFallback'));
    setConfigName(baseName.slice(0, MAX_NAME_LENGTH));
    setAmountPerMarket('1');
    setInvestmentTotalLimit('');
    setStopLossEnabled(false);
    setStopLossLimit('');
    setCopySellEnabled(true);
    setMarketCooldownEnabled(false);
    setMarketCooldownMinutes(String(DEFAULT_MARKET_COOLDOWN_MINUTES));
    setSaving(false);
    setCopied(false);
  }, [open, isPortfolio, traderCount, primaryTrader?.address, primaryTrader?.name, t]);

  const trimmedName = useMemo(() => configName.trim(), [configName]);
  const nameTooLong = trimmedName.length > MAX_NAME_LENGTH;
  const nameValid = trimmedName.length > 0 && !nameTooLong;

  const amountValue = Number(amountPerMarket);
  const amountValid = Number.isFinite(amountValue) && amountValue >= MIN_AMOUNT;

  const limitValue = investmentTotalLimit === '' ? null : Number(investmentTotalLimit);
  const limitValid =
    investmentTotalLimit === '' || (Number.isFinite(limitValue) && limitValue >= MIN_AMOUNT);
  const limitTooLow =
    limitValid && investmentTotalLimit !== '' && amountValid && limitValue < amountValue;

  const stopLossValue = stopLossLimit === '' ? null : Number(stopLossLimit);
  const stopLossValid =
    !stopLossEnabled || stopLossLimit === '' || (Number.isFinite(stopLossValue) && stopLossValue >= MIN_AMOUNT);
  const marketCooldownValue = marketCooldownMinutes === '' ? null : Number(marketCooldownMinutes);
  const marketCooldownValid =
    !marketCooldownEnabled
    || (
      Number.isInteger(marketCooldownValue)
      && marketCooldownValue >= MIN_MARKET_COOLDOWN_MINUTES
      && marketCooldownValue <= MAX_MARKET_COOLDOWN_MINUTES
    );

  const canSubmit =
    resolvedTraders.length > 0
    && nameValid
    && amountValid
    && limitValid
    && !limitTooLow
    && stopLossValid
    && marketCooldownValid
    && !saving;

  const metrics = useMemo(() => {
    if (!primaryTrader || isPortfolio) return [];
    const items = [];
    if (Number.isFinite(primaryTrader.winRate)) {
      items.push({ label: t('copyConfigModal.winRate'), value: formatPercent(primaryTrader.winRate) });
    }
    if (Number.isFinite(primaryTrader.totalProfit)) {
      items.push({ label: t('copyConfigModal.totalProfit'), value: formatUSD(primaryTrader.totalProfit) });
    }
    if (Number.isFinite(primaryTrader.aiScore)) {
      items.push({ label: t('copyConfigModal.aiScore'), value: Number(primaryTrader.aiScore).toFixed(1) });
    }
    if (primaryTrader.tradingStyle) {
      items.push({ label: t('copyConfigModal.tradingStyle'), value: primaryTrader.tradingStyle });
    }
    return items;
  }, [primaryTrader, isPortfolio, t]);

  const handleCopyAddress = async () => {
    if (!resolvedTraders.length) return;
    try {
      const addresses = resolvedTraders.map((item) => item.address).filter(Boolean);
      const payload = isPortfolio ? addresses.join('\n') : addresses[0];
      if (!payload) return;
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      pushToast(
        isPortfolio ? t('copyConfigModal.addressesCopied') : t('copyConfigModal.addressCopied'),
        'success'
      );
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error('[Copy address]', error);
      pushToast(t('copyConfigModal.copyFailed'), 'error');
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const traderAddresses = resolvedTraders.map((item) => item.address).filter(Boolean);
      const payload = {
        config_name: trimmedName,
        trader_addresses: traderAddresses,
        config_type: isPortfolio ? 'portfolio' : 'single',
        amount_per_market: amountValue,
        investment_total_limit: investmentTotalLimit === '' ? null : limitValue,
        stop_loss_enabled: stopLossEnabled,
        stop_loss_limit: stopLossEnabled && stopLossLimit !== '' ? stopLossValue : null,
        copy_sell_enabled: copySellEnabled,
        market_cooldown_enabled: marketCooldownEnabled,
        market_cooldown_minutes: marketCooldownEnabled
          ? (marketCooldownMinutes === '' ? null : marketCooldownValue)
          : null,
        auto_copy_enabled: true,
      };
      const response = await createCopyConfig(payload);
      if (response?.code === 0) {
        pushToast(t('copyConfigModal.createSuccess'), 'success');
        onCreated?.(response.data);
        onClose();
      } else {
        pushToast(response?.msg || t('copyConfigModal.createFailed'), 'error');
      }
    } catch (error) {
      if (error?.isMembershipRequired) {
        onRequireMembership?.(error.currentTier);
        return;
      }
      console.error('[Create copy config]', error);
      pushToast(error.message || t('copyConfigModal.createFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('copyConfigModal.title')}
      subtitle={t('copyConfigModal.subtitle')}
      maxWidthClass="max-w-[560px]"
    >
      <div className="flex flex-col gap-5">
        {/* Trader Info Card */}
        <div className="rounded-xl border border-slate-700/50 bg-slate-900/40 p-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center shrink-0">
              <FiShield className="w-5 h-5 text-sky-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-100 truncate">
                  {isPortfolio
                    ? t('copyConfigModal.portfolioTitle')
                    : (primaryTrader?.name || t('copyConfigModal.unknownTrader'))}
                </p>
                {!isPortfolio && primaryTrader?.secondaryName ? (
                  <span className="text-xs text-slate-500 truncate">({primaryTrader.secondaryName})</span>
                ) : null}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                {isPortfolio ? (
                  <span>{t('copyConfigModal.portfolioCount', { count: traderCount || 0 })}</span>
                ) : (
                  <span className="font-mono">{shortAddress(primaryTrader?.address)}</span>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 transition-colors"
                  onClick={handleCopyAddress}
                >
                  <FiCopy className="w-3 h-3" />
                  {copied
                    ? t('common.copied')
                    : (isPortfolio ? t('copyConfigModal.copyAllAddresses') : t('copyConfigModal.copyAddress'))}
                </button>
              </div>
            </div>
          </div>

          {metrics.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-4">
              {metrics.map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-slate-800/60 bg-slate-950/40 px-2 py-2 text-center"
                >
                  <p className="text-[10px] text-slate-500 mb-0.5">{item.label}</p>
                  <p className="text-xs font-semibold text-slate-200">{item.value}</p>
                </div>
              ))}
            </div>
          )}

          {isPortfolio && resolvedTraders.length > 0 && (
            <div className="mt-4 rounded-lg border border-slate-800/60 bg-slate-950/40 px-3 py-2.5">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                <span>{t('copyConfigModal.addressesLabel')}</span>
                <span className="text-sky-400">{traderCount}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                {resolvedTraders.map((item) => (
                  <span
                    key={item.address}
                    className="px-2 py-0.5 rounded-md text-[10px] font-mono text-slate-400 bg-slate-900/70 border border-slate-700/50"
                  >
                    {shortAddress(item.address)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Config Name */}
          <div>
            <label className="text-xs font-medium text-slate-400 mb-2 block">
              {t('copyConfigModal.nameLabel')}
            </label>
            <input
              className={`w-full rounded-lg bg-slate-950/60 border px-3 py-2.5 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-600 ${
                nameValid
                  ? 'border-slate-700/60 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20'
                  : 'border-rose-500/70 focus:border-rose-500/80 focus:ring-2 focus:ring-rose-500/20'
              }`}
              placeholder={t('copyConfigModal.namePlaceholder')}
              value={configName}
              maxLength={MAX_NAME_LENGTH}
              onChange={(event) => setConfigName(event.target.value)}
            />
            {!nameValid && (
              <p className="text-xs text-rose-400 mt-1.5">
                {nameTooLong ? t('copyConfigModal.nameTooLong') : t('copyConfigModal.nameRequired')}
              </p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="text-xs font-medium text-slate-400 mb-2 block">
              {t('copyConfigModal.amountLabel')}
            </label>
            <input
              type="number"
              min={MIN_AMOUNT}
              step="0.1"
              className={`w-full rounded-lg bg-slate-950/60 border px-3 py-2.5 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-600 ${
                amountValid
                  ? 'border-slate-700/60 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20'
                  : 'border-rose-500/70 focus:border-rose-500/80 focus:ring-2 focus:ring-rose-500/20'
              }`}
              placeholder={t('copyConfigModal.amountPlaceholder')}
              value={amountPerMarket}
              onChange={(event) => setAmountPerMarket(event.target.value)}
            />
            {!amountValid ? (
              <p className="text-xs text-rose-400 mt-1.5">{t('copyConfigModal.amountInvalid')}</p>
            ) : (
              <p className="text-xs text-slate-500 mt-1.5">{t('copyConfigModal.amountHint')}</p>
            )}
          </div>

          {/* Advanced Settings Toggle */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setAdvancedOpen((prev) => !prev)}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg border border-slate-700/50 bg-slate-900/30 text-left transition-all hover:bg-slate-800/30"
            >
              <span className="text-xs font-medium text-slate-400">
                {t('copyConfigModal.advancedSettings')}
              </span>
              <FiChevronDown
                className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${advancedOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Advanced Settings Content */}
            {advancedOpen && (
              <div className="mt-3 space-y-4">
                {/* Investment Limit */}
                <div>
                  <label className="text-xs font-medium text-slate-400 mb-2 block">
                    {t('copyConfigModal.investmentLimitLabel')}
                  </label>
                  <input
                    type="number"
                    min={MIN_AMOUNT}
                    step="0.1"
                    className={`w-full rounded-lg bg-slate-950/60 border px-3 py-2.5 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-600 ${
                      limitValid && !limitTooLow
                        ? 'border-slate-700/60 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20'
                        : 'border-rose-500/70 focus:border-rose-500/80 focus:ring-2 focus:ring-rose-500/20'
                    }`}
                    placeholder={t('copyConfigModal.investmentLimitPlaceholder')}
                    value={investmentTotalLimit}
                    onChange={(event) => setInvestmentTotalLimit(event.target.value)}
                  />
                  {!limitValid && (
                    <p className="text-xs text-rose-400 mt-1.5">{t('copyConfigModal.investmentLimitInvalid')}</p>
                  )}
                  {limitTooLow && (
                    <p className="text-xs text-rose-400 mt-1.5">{t('copyConfigModal.investmentLimitTooLow')}</p>
                  )}
                  {limitValid && !limitTooLow && (
                    <p className="text-xs text-slate-500 mt-1.5">{t('copyConfigModal.investmentLimitHint')}</p>
                  )}
                </div>

                {/* Copy Side */}
                <div>
                  <label className="text-xs font-medium text-slate-400 mb-2 block">
                    {t('copyConfigModal.copySideLabel')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCopySellEnabled(true)}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                        copySellEnabled
                          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                          : 'bg-slate-900/60 text-slate-400 border-slate-700/60 hover:text-slate-300'
                      }`}
                    >
                      {t('copyConfigModal.copySideBuySell')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCopySellEnabled(false)}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                        !copySellEnabled
                          ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                          : 'bg-slate-900/60 text-slate-400 border-slate-700/60 hover:text-slate-300'
                      }`}
                    >
                      {t('copyConfigModal.copySideBuyOnly')}
                    </button>
                  </div>
                </div>

                {/* Stop Loss */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-slate-400">
                      {t('copyConfigModal.stopLossLabel')}
                    </label>
                    <button
                      type="button"
                      className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-all ${
                        stopLossEnabled
                          ? 'bg-sky-500/15 text-sky-300 border-sky-500/40'
                          : 'bg-slate-800/60 text-slate-500 border-slate-700/60'
                      }`}
                      onClick={() => setStopLossEnabled((prev) => !prev)}
                    >
                      {stopLossEnabled ? t('copyConfigModal.stopLossOn') : t('copyConfigModal.stopLossOff')}
                    </button>
                  </div>
                  <input
                    type="number"
                    min={MIN_AMOUNT}
                    step="0.1"
                    disabled={!stopLossEnabled}
                    className={`w-full rounded-lg bg-slate-950/60 border px-3 py-2.5 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-600 ${
                      stopLossEnabled
                        ? stopLossValid
                          ? 'border-slate-700/60 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20'
                          : 'border-rose-500/70 focus:border-rose-500/80 focus:ring-2 focus:ring-rose-500/20'
                        : 'border-slate-800/50 text-slate-500 cursor-not-allowed'
                    }`}
                    placeholder={t('copyConfigModal.stopLossPlaceholder')}
                    value={stopLossLimit}
                    onChange={(event) => setStopLossLimit(event.target.value)}
                  />
                  {!stopLossValid && (
                    <p className="text-xs text-rose-400 mt-1.5">{t('copyConfigModal.stopLossInvalid')}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1.5">{t('copyConfigModal.stopLossHint')}</p>
                </div>

                {/* Market Cooldown */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="inline-flex items-center gap-2">
                      <label className="inline-flex items-center text-xs font-medium leading-none text-slate-400">
                        {t('copyConfigModal.marketCooldownLabel')}
                      </label>
                      <div className="relative group inline-flex items-center">
                        <FiHelpCircle className="h-4 w-4 text-slate-400" />
                        <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-md border border-slate-700/60 bg-slate-900/95 px-2.5 py-2 text-[11px] leading-relaxed text-slate-300 opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100">
                          {t('copyConfigModal.marketCooldownTooltip')}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-all ${
                        marketCooldownEnabled
                          ? 'bg-sky-500/15 text-sky-300 border-sky-500/40'
                          : 'bg-slate-800/60 text-slate-500 border-slate-700/60'
                      }`}
                      onClick={() => setMarketCooldownEnabled((prev) => !prev)}
                    >
                      {marketCooldownEnabled
                        ? t('copyConfigModal.marketCooldownOn')
                        : t('copyConfigModal.marketCooldownOff')}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      min={MIN_MARKET_COOLDOWN_MINUTES}
                      max={MAX_MARKET_COOLDOWN_MINUTES}
                      step="1"
                      disabled={!marketCooldownEnabled}
                      className={`w-full rounded-lg bg-slate-950/60 border px-3 py-2.5 pr-16 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-600 ${
                        marketCooldownEnabled
                          ? marketCooldownValid
                            ? 'border-slate-700/60 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20'
                            : 'border-rose-500/70 focus:border-rose-500/80 focus:ring-2 focus:ring-rose-500/20'
                          : 'border-slate-800/50 text-slate-500 cursor-not-allowed'
                      }`}
                      placeholder={t('copyConfigModal.marketCooldownPlaceholder')}
                      value={marketCooldownMinutes}
                      onChange={(event) => setMarketCooldownMinutes(event.target.value)}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                      {t('copyConfigModal.minuteUnit')}
                    </span>
                  </div>
                  {!marketCooldownValid && (
                    <p className="text-xs text-rose-400 mt-1.5">
                      {t('copyConfigModal.marketCooldownInvalid', {
                        min: MIN_MARKET_COOLDOWN_MINUTES,
                        max: MAX_MARKET_COOLDOWN_MINUTES,
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2.5 pt-2">
          <Button variant="secondary" size="medium" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" size="medium" onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? t('copyConfigModal.creating') : t('copyConfigModal.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
